// The full-screen modes that replace the match layout: menu, new-game setup,
// spectator, multiplayer lobby and the online match. Extracted from App.tsx
// (ROADMAP 7.14); `hasModeScreen` mirrors the order of the old early returns.

import { CoachmarkHost } from '../coachmark/CoachmarkHost.js';
import type { useCoachmarks } from '../coachmark/useCoachmarks.js';
import { MenuScreen } from '../components/MenuScreen.js';
import { LobbyScreen } from '../multiplayer/lobby/LobbyScreen.js';
import { MultiplayerMatchView } from '../multiplayer/match/MultiplayerMatchView.js';
import type { AutosaveEntry } from '../save/autosaveStore.js';
import { SetupScreen } from '../setup/SetupScreen.js';
import { loadLastSetup } from '../setup/lastSetup.js';
import type { SetupDraft } from '../setup/setupModel.js';
import { SpectatorView } from '../spectate/SpectatorView.js';
import type { useGameFlow } from './useGameFlow.js';

type GameFlow = ReturnType<typeof useGameFlow>;

/** True when the current mode owns the whole screen (no match layout). */
export function hasModeScreen({ gameMode, watchId, mpMatchInfo }: GameFlow): boolean {
  return (
    gameMode === 'menu' ||
    gameMode === 'setup' ||
    (gameMode === 'spectate' && !!watchId) ||
    gameMode === 'multiplayer-lobby' ||
    (gameMode === 'multiplayer-match' && !!mpMatchInfo)
  );
}

interface ModeScreenProps {
  flow: GameFlow;
  coachmarkController: ReturnType<typeof useCoachmarks>;
  continueSave: AutosaveEntry | null;
  onLoadSave: (json: string) => void;
  lastSetup: SetupDraft | null;
  setLastSetup: (draft: SetupDraft | null) => void;
  onStartFromSetup: (draft: SetupDraft) => void;
  onSetPlayerName: (name: string) => void;
}

export function ModeScreen({
  flow,
  coachmarkController,
  continueSave,
  onLoadSave,
  lastSetup,
  setLastSetup,
  onStartFromSetup,
  onSetPlayerName,
}: ModeScreenProps) {
  const {
    watchId,
    setWatchId,
    gameMode,
    setGameMode,
    localPlayerName,
    mpMatchInfo,
    setMpMatchInfo,
    mpLobby,
  } = flow;

  if (gameMode === 'menu') {
    return (
      <>
        <MenuScreen
          continueSave={continueSave}
          onContinue={() => {
            if (!continueSave) return;
            onLoadSave(continueSave.json);
            setGameMode('single');
          }}
          onStartSingle={() => {
            setLastSetup(loadLastSetup());
            setGameMode('setup');
          }}
          onStartMultiplayer={() => setGameMode('multiplayer-lobby')}
        />
        {/* Same shared controller as the match branch so the start-game
            coachmark can open against the menu's Start Game button while
            the menu is mounted. */}
        <CoachmarkHost controller={coachmarkController} />
      </>
    );
  }

  // ── New game setup ──
  if (gameMode === 'setup') {
    return (
      <SetupScreen
        initialDraft={lastSetup}
        onStart={onStartFromSetup}
        onBack={() => setGameMode('menu')}
      />
    );
  }

  // ── Spectator (read-only) ──
  if (gameMode === 'spectate' && watchId) {
    return (
      <SpectatorView
        matchId={watchId}
        onBack={() => {
          setWatchId(null);
          setGameMode('menu');
          window.history.replaceState(null, '', window.location.pathname);
        }}
      />
    );
  }

  // ── Multiplayer lobby ──
  if (gameMode === 'multiplayer-lobby') {
    return (
      <LobbyScreen
        mp={mpLobby}
        onWatch={(id) => {
          setWatchId(id);
          setGameMode('spectate');
        }}
        onBack={() => setGameMode('menu')}
        localPlayerName={localPlayerName}
        onSetPlayerName={onSetPlayerName}
      />
    );
  }

  // ── Multiplayer match ──
  if (gameMode === 'multiplayer-match' && mpMatchInfo) {
    return (
      <MultiplayerMatchView
        matchInfo={mpMatchInfo}
        onBackToMenu={() => {
          setMpMatchInfo(null);
          setGameMode('menu');
        }}
        onRematch={() => {
          mpLobby.rematch();
          setMpMatchInfo(null);
          setGameMode('multiplayer-lobby');
        }}
      />
    );
  }

  return null;
}
